import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

/**
 * The message balloon. `side` drives both the row layout (avatar/bubble order,
 * justification) and the bubble surface colors. The corner nearest the avatar is
 * rounded less (`rounded-bl-sm` / `rounded-br-sm`) to read as a chat tail.
 *
 * Presentational only — no realtime, no domain strings. Avatar, name, time, and
 * message body all arrive via props/children.
 */
const chatBubbleVariants = cva(
  'w-fit max-w-full rounded-2xl px-4 py-2.5 text-sm break-words',
  {
    variants: {
      side: {
        inbound: 'rounded-bl-sm bg-muted text-foreground',
        outbound: 'rounded-br-sm bg-primary text-primary-foreground',
      },
    },
    defaultVariants: {
      side: 'inbound',
    },
  },
);

type ChatSide = NonNullable<VariantProps<typeof chatBubbleVariants>['side']>;

export interface ChatBubbleProps extends Omit<React.ComponentProps<'div'>, 'children'> {
  side: ChatSide;
  avatar?: React.ReactNode;
  name?: React.ReactNode;
  time?: React.ReactNode;
  /**
   * Per-message controls (reply / forward / edit / delete) revealed on hover or
   * keyboard focus. Rendered *below* the balloon on the same row as the
   * timestamp — the Telegram placement — and faded in rather than mounted, so
   * the thread never reflows as the pointer moves down it.
   */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

function ChatBubble({
  className,
  side,
  avatar,
  name,
  time,
  actions,
  children,
  ...props
}: ChatBubbleProps) {
  const isOutbound = side === 'outbound';
  return (
    <div
      data-slot="chat-bubble"
      data-side={side}
      className={cn(
        // `justify-start` in BOTH directions: on the outbound row the axis is
        // already reversed by `flex-row-reverse`, so "start" is the right edge.
        // (`justify-end` here would push the bubble back to the LEFT.)
        'group/chat-bubble flex items-end justify-start gap-2',
        isOutbound && 'flex-row-reverse',
        className,
      )}
      {...props}
    >
      {avatar}
      <div
        className={cn(
          // The width cap lives on the column (a definite % of the row), not on
          // the balloon: a % max-width on a shrink-wrapped child resolves against
          // the column's own content size and squeezes long messages narrow.
          'flex max-w-[min(75%,32rem)] min-w-0 flex-col gap-1',
          isOutbound ? 'items-end' : 'items-start',
        )}
      >
        {name !== undefined ? (
          <span
            data-slot="chat-bubble-name"
            className="px-1 text-xs font-medium tracking-tight text-muted-foreground"
          >
            {name}
          </span>
        ) : null}
        <div data-slot="chat-bubble-body" className={cn(chatBubbleVariants({ side }))}>
          {children}
        </div>

        {/* Metadata row under the balloon: the timestamp, plus the action
         * controls that fade in on hover — the Telegram placement, where the
         * controls sit below the message rather than crowding its edge. The row
         * is always mounted (with a reserved min-height) so revealing the
         * controls never nudges the thread. */}
        {time !== undefined || actions !== undefined ? (
          <div
            className={cn(
              'flex min-h-6 items-center gap-1 px-1',
              isOutbound ? 'flex-row-reverse' : 'flex-row',
            )}
          >
            {time !== undefined ? (
              <span
                data-slot="chat-bubble-time"
                className="text-xs tracking-tight text-muted-foreground"
              >
                {time}
              </span>
            ) : null}
            {actions !== undefined ? (
              <div
                data-slot="chat-bubble-actions"
                className={cn(
                  'flex shrink-0 items-center gap-0.5',
                  // Hidden until the row is hovered or something inside it has
                  // keyboard focus. `invisible` (not `hidden`) keeps the box in
                  // layout so nothing shifts when the controls appear.
                  'invisible opacity-0 transition-opacity duration-150 ease-out',
                  'group-hover/chat-bubble:visible group-hover/chat-bubble:opacity-100',
                  'group-focus-within/chat-bubble:visible group-focus-within/chat-bubble:opacity-100',
                )}
              >
                {actions}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { ChatBubble, chatBubbleVariants };
